import type { Droppable, ReadonlyNonEmptyArray } from '../../../utils'

import {
    Capability,
    type ConnectwareError,
    createIsAuthenticatedWithCapabilitiesSelector,
    type LoadableTopicsChunk,
    selecExplorerExistingTopicsChunkWithUnloaded,
    selecExplorerTopicsChunk,
    selectExplorerChunks,
    selectExplorerLastTopicsChunkNextReference,
    type Topic,
    type TopicTree,
} from '../../../domain'

import { ExplorerUsecase } from './Explorer'

const canReadTopicsMetadata = createIsAuthenticatedWithCapabilitiesSelector(Capability.TOPIC_CHUNKS_READ)

export class ExplorerChunksUsecase extends ExplorerUsecase {
    private setChunks (prefix: TopicTree['fullPath'], value: ReadonlyNonEmptyArray<LoadableTopicsChunk>): void {
        const chunks = new Map(selectExplorerChunks(this.getState()))
        chunks.set(prefix, value)
        this.setExplorer({ chunks })
    }

    private initializeChunk (prefix: TopicTree['fullPath']): number {
        /** Initalize current chunk and save the position of the chunk that has been just to 'loading' */
        const currentChunk = selecExplorerTopicsChunk(this.getState(), prefix)

        this.setChunks(prefix, [...currentChunk, null])

        return currentChunk.length
    }

    load (prefix: TopicTree['fullPath'], droppable: Pick<Droppable, 'isDropped'>): void {
        const state = this.getState()

        if (!canReadTopicsMetadata(state)) {
            /**
             * Drop loading silently if the current state does not have permission
             */
            return
        }

        /**
         * Fetch the needed metadata
         */
        const reference = selectExplorerLastTopicsChunkNextReference(state, prefix)

        /**
         * Add a chunk with the loading state
         */
        const position = this.initializeChunk(prefix)

        void this.topicsService
            .fetchTopicsChunk(prefix, reference)
            .catch((e: ConnectwareError) => e)
            .then((chunkOrerror) => {
                if (droppable.isDropped) {
                    return
                }

                /**
                 * Update the chunk with the response
                 */
                const [...next] = selecExplorerExistingTopicsChunkWithUnloaded(this.getState(), prefix, position)
                next[position] = chunkOrerror
                this.setChunks(prefix, next as typeof next & ReturnType<typeof selecExplorerExistingTopicsChunkWithUnloaded>)
            })
    }

    createRootTopic (): Topic {
        return this.topicsService.createTopic(null)
    }
}
